import React, { useState, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { get, post } from "../../services/api"; // Adjust import based on your API helper location

function AttendanceNotificationModal({
  onClose,
  parentId,
  StudentFirstRegistrationId,
}) {
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const [templatesData, setTemplatesData] = useState([]);
  const [notifTypesData, setNotifTypesData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [rolesData, setRolesData] = useState([]);
  const [placeholdersData, setPlaceholdersData] = useState([]);
  const [subCategoryData, setSubCategoryData] = useState([]);
  const [navFormData, setNavFormData] = useState([]);
  const [formEventsData, setFormEventsData] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [activeEditorIndex, setActiveEditorIndex] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [isImmediate, setIsImmediate] = useState(false);
  const [usersData, setUsersData] = useState([]);
  const [selectedMode, setSelectedMode] = useState("roles");
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [highlightedRow, setHighlightedRow] = useState(null);

  const quillRefs = useRef([]);
  const [searchParams] = useSearchParams();
  const notificationTemplateId = searchParams.get("id");
  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    watch,
  } = useForm({
    defaultValues: {
      id: 0,
      userId: userId,
      title: "",
      notificationTemplates: [],
      formCategoryId: "",
      FormSubCategoryId: "",
      formId: "",
      roleIds: "",
      notificationFormEventId: "",
      isActive: true,
      placeholderId: null,
    },
  });
  const notificationTypeIds =
    watch("notificationTemplates")?.map(
      (template) => template.notificationTypeId
    ) || [];
  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ header: 1 }, { header: 2 }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ direction: "rtl" }],
      [{ size: ["small", false, "large", "huge"] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ["clean"],
    ],
  };

  // Fetch categories
  const getCategories = async () => {
    if (!token) {
      toast.error("Unauthorized. Please log in.");
      return;
    }
    try {
      const response = await get("UserAuthentication/GetAllCategory", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response?.navCategory) {
        setCategoryData(response.navCategory);
      } else {
        toast.error("Failed to fetch categories.");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Error fetching categories.");
    }
  };

  // Fetch subcategories
  const getSubCategoryList = async (categoryId) => {
    if (!token || !categoryId) {
      setSubCategoryData([]);
      setNavFormData([]);
      setFormEventsData([]);
      return;
    }
    try {
      const formdata = { formCategoryId: categoryId };
      const response = await post(
        `UserAuthentication/GetAllNavSubCategory`,
        formdata,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response) {
        setSubCategoryData(response);
      } else {
        toast.error("Failed to fetch subcategories.");
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      toast.error("Error fetching subcategories.");
    }
  };

  // Fetch forms
  const getNavFormList = async (subCategoryId) => {
    if (!token || !subCategoryId) {
      setNavFormData([]);
      setFormEventsData([]);
      return;
    }
    try {
      const formdata = { id: subCategoryId };
      const response = await post(
        `UserAuthentication/GetAllNavForm`,
        formdata,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response) {
        setNavFormData(response);
      } else {
        toast.error("Failed to fetch forms.");
      }
    } catch (error) {
      console.error("Error fetching forms:", error);
      toast.error("Error fetching forms.");
    }
  };

  // Fetch notification types
  const fetchNotifTypes = async () => {
    if (!token) {
      toast.error("Unauthorized. Please log in.");
      return;
    }
    try {
      const response = await get("Notification/types", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response?.success) {
        setNotifTypesData(response?.notificationTypes);
      } else {
        toast.error("Failed to fetch notification types.");
      }
    } catch (error) {
      console.error("Error fetching notification types:", error);
      toast.error("Error fetching notification types.");
    }
  };

  // Fetch notification templates
  const fetchTemplates = async () => {
    if (!token) {
      toast.error("Unauthorized. Please log in.");
      return;
    }
    try {
      const response = await get("Notification/templates", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response?.success) {
        setTemplatesData(response?.notificationTemplates);
      } else {
        toast.error("Failed to fetch templates.");
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Error fetching templates.");
    }
  };

  // Fetch roles
  const fetchRoles = async () => {
    if (!token) {
      toast.error("Unauthorized. Please log in.");
      return;
    }
    try {
      const response = await get("Role", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response) {
        setRolesData(response);
      } else {
        toast.error("Failed to fetch roles.");
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Error fetching roles.");
    }
  };

  // Fetch placeholders
  const fetchPlaceholders = async () => {
    if (!token) {
      toast.error("Unauthorized. Please log in.");
      return;
    }
    try {
      const response = await get("Notification/placeholders", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response?.success && response?.notificationPlaceholders) {
        setPlaceholdersData(response.notificationPlaceholders);
      } else {
        toast.error("Failed to fetch placeholders.");
      }
    } catch (error) {
      console.error("Error fetching placeholders:", error);
      toast.error("Error fetching placeholders.");
    }
  };

  // Fetch form events
  const fetchFormEvents = async (formId) => {
    if (!token || !formId) {
      setFormEventsData([]);
      return;
    }
    try {
      const response = await get(
        `Notification/formEventTypes/?formId=${formId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response?.success && response?.notificationFormEvents) {
        setFormEventsData(response.notificationFormEvents);
      } else {
        toast.error("Failed to fetch form events.");
      }
    } catch (error) {
      console.error("Error fetching form events:", error);
      toast.error("Error fetching form events.");
    }
  };

  // Fetch users based on roleId
  const fetchUsers = async (roleId) => {
    if (!token || !roleId) {
      toast.error("Missing token or role ID.");
      setUsersData([]);
      setValue("userIds", "");
      return;
    }
    try {
      const response = await get(`User/GetUserByIds?ids=${parentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("fetchUsers response:", response); // Debugging
      const users = response.users || response.user || [];
      if (users.length > 0) {
        setUsersData(users);
        const emails = users
          .map((user) => user.email)
          .filter((email) => email)
          .join(", ");
        console.log("Setting userIds to:", emails); // Debugging
        setValue("userIds", emails || "");
      } else {
        setUsersData([]);
        setValue("userIds", "");
        toast.warn("No users found for the selected role.");
      }
    } catch (error) {
      console.error(
        "Error fetching users:",
        error.response?.data || error.message
      );
      toast.error(
        "Error fetching users: " +
          (error.response?.data?.Message || error.message)
      );
      setUsersData([]);
      setValue("userIds", "");
    }
  };

  useEffect(() => {
    getCategories();
    fetchNotifTypes();
    fetchTemplates();
    fetchRoles();
    fetchPlaceholders();
    if (notificationTemplateId != 0) {
      setHighlightedRow(notificationTemplateId);
    }
  }, []);

  useEffect(() => {
    if (selectedCategoryId) {
      getSubCategoryList(selectedCategoryId);
    } else {
      setSubCategoryData([]);
      setNavFormData([]);
      setFormEventsData([]);
      setValue("FormSubCategoryId", "");
      setValue("formId", "");
      setValue("notificationFormEventId", "");
    }
  }, [selectedCategoryId, setValue]);

  useEffect(() => {
    if (selectedSubCategoryId) {
      getNavFormList(selectedSubCategoryId);
    } else {
      setNavFormData([]);
      setFormEventsData([]);
      setValue("formId", "");
      setValue("notificationFormEventId", "");
    }
  }, [selectedSubCategoryId, setValue]);

  useEffect(() => {
    setIsImmediate(true);
    setSelectedMode("users");
  }, []);

  // useEffect to fetch users when rolesData or parentId changes
  useEffect(() => {
    if (rolesData.length > 0 && parentId) {
      console.log("rolesData:", rolesData); // Debugging
      console.log("parentId:", parentId); // Debugging
      const parentRole = rolesData.find((role) =>
        role.name.toLowerCase().includes("parent")
      );
      if (parentRole) {
        setValue("roleIds", parentRole.id.toString());
        fetchUsers(parentRole.id);
      } else {
        console.warn("Parent role not found in rolesData:", rolesData);
        toast.warn("Parent role not found.");
        setValue("userIds", "");
      }
    }
  }, [rolesData, parentId, setValue]);

  const handleCategoryChange = (selectedOption) => {
    const categoryId = selectedOption ? selectedOption.value : "";
    setValue("formCategoryId", categoryId);
    setSelectedCategoryId(categoryId);
    setSelectedSubCategoryId(null);
    setValue("FormSubCategoryId", "");
    setValue("formId", "");
    setValue("notificationFormEventId", "");
    trigger("formCategoryId");
  };

  const handleSubCategoryChange = (selectedOption) => {
    const subCategoryId = selectedOption ? selectedOption.value : "";
    setValue("FormSubCategoryId", subCategoryId);
    setSelectedSubCategoryId(subCategoryId);
    setValue("formId", "");
    setValue("notificationFormEventId", "");
    trigger("FormSubCategoryId");
  };

  const handleFormChange = (selectedOption) => {
    const formId = selectedOption ? selectedOption.value : "";
    setValue("formId", formId);
    setValue("notificationFormEventId", "");
    if (formId) {
      fetchFormEvents(formId);
    } else {
      setFormEventsData([]);
    }
    trigger("formId");
  };

  const handleNotificationTypeChange = (selectedOptions) => {
    const typeIds = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    const currentTemplates = watch("notificationTemplates") || [];

    const newTemplates = typeIds.map((typeId) => {
      const existingTemplate = currentTemplates.find(
        (t) => t.notificationTypeId === typeId
      );
      return (
        existingTemplate || {
          notificationTypeId: typeId,
          subject: "",
          body: "",
        }
      );
    });

    setValue("notificationTemplates", newTemplates);
    trigger("notificationTemplates");
  };

  const handleFormEventChange = (selectedOption) => {
    const eventId = selectedOption ? selectedOption.value : "";
    setValue("notificationFormEventId", eventId);
    trigger("notificationFormEventId");
  };

  const handleRoleChange = (selectedOptions) => {
    let roleIds = "";
    if (selectedMode === "roles") {
      roleIds = selectedOptions
        ? selectedOptions.map((option) => option.value).join(",")
        : "";
    } else {
      roleIds = selectedOptions ? String(selectedOptions.value) : "";
    }
    setValue("roleIds", roleIds);
    setValue("userIds", "");
    setUsersData([]);
    if (roleIds && selectedMode === "users") {
      fetchUsers(roleIds);
    }
    trigger("roleIds");
  };

  const handlePlaceholderChange = (selectedOption) => {
    if (!selectedOption) return;

    let targetEditorIndex = activeEditorIndex;
    if (
      targetEditorIndex === null &&
      watch("notificationTemplates").length > 0
    ) {
      targetEditorIndex = 0;
      setActiveEditorIndex(0);
    }

    if (targetEditorIndex === null || !quillRefs.current[targetEditorIndex]) {
      console.warn("No valid editor available:", {
        targetEditorIndex,
        quillRefs,
      });
      toast.error("Please select a template source first.");
      return;
    }

    const placeholderId = selectedOption.value;
    const placeholder = placeholdersData.find(
      (p) => p.id === parseInt(placeholderId)
    );
    if (!placeholder || !placeholder.placeholderValue) {
      console.error("Placeholder not found or missing placeholderValue:", {
        placeholderId,
        placeholdersData,
      });
      toast.error("Failed to insert placeholder. Please try again.");
      return;
    }

    const quill = quillRefs.current[targetEditorIndex].getEditor();
    quill.focus();
    const selection = quill.getSelection(true);
    const cursorPosition = selection ? selection.index : quill.getLength() || 0;

    quill.insertText(cursorPosition, placeholder.placeholderValue);

    setValue(
      `notificationTemplates.${targetEditorIndex}.body`,
      quill.root.innerHTML,
      { shouldValidate: true }
    );

    setValue("placeholderId", null);
  };

  const handleEditorFocus = (index) => {
    setActiveEditorIndex(index);
  };

  const saveData = async (data) => {
    if (!token || !userId) {
      toast.error("Unauthorized. Please log in.");
      return;
    }
    setHighlightedRow(null);
    if (isImmediate) {
      setShowProcessingModal(true);
    }

    try {
      for (const template of data.notificationTemplates) {
        const isEmail =
          notifTypesData
            .find((type) => type.id === template.notificationTypeId)
            ?.title.toLowerCase() === "email";

        if (isEmail && !template.subject) {
          toast.error("Subject is required for Email source.");
          if (isImmediate) {
            setShowProcessingModal(false);
          }
          return;
        }

        const notificationTemplate = {
          notificationTypeId: template.notificationTypeId,
          title: data.title,
          subject: isEmail ? template.subject : "",
          body: template.body,
          roleIds: data.roleIds || "",
          isActive: data.isActive,
          isImmediate: isImmediate,
        };

        if (!isImmediate) {
          notificationTemplate.formCategoryId =
            parseInt(data.formCategoryId) || null;
          notificationTemplate.formSubcategoryId =
            parseInt(data.FormSubCategoryId) || null;
          notificationTemplate.formId = parseInt(data.formId) || null;
          notificationTemplate.notificationFormEventId =
            parseInt(data.notificationFormEventId) || null;
        }
        const payload = {
          id: data.id,
          userId: data.userId,
          userIds: parentId || "",
          studentFirstRegistrationId: StudentFirstRegistrationId,
          additionalEmails: isImmediate ? data.additionalEmails || "" : "",
          notificationTemplate,
        };

        const response = await post("Notification/savetemplate", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.success) {
          toast.error(
            response.message ||
              `Failed to save template for ${
                notifTypesData.find(
                  (type) => type.id === template.notificationTypeId
                )?.title
              } source.`
          );
          if (isImmediate) {
            setShowProcessingModal(false);
          }
          return;
        }
      }

      toast.success("Success!");
      // reset();
      // setSelectedCategoryId(null);
      // setSelectedSubCategoryId(null);
      // setActiveEditorIndex(null);
      // setIsEditing(false);
      // setIsImmediate(false);
      // setUsersData([]);
      // setSelectedMode("roles");
      // await fetchTemplates();
      // if (isImmediate) {
      //   setShowProcessingModal(true);
      // }
    } catch (error) {
      console.error("Error saving:", error?.response?.data?.Message);
      toast.error(error?.response?.data?.Message || "Error saving template.");
      if (isImmediate) {
        setShowProcessingModal(false);
      }
    }
  };

  return (
    <>
      <div
        className="modal fade show"
        tabIndex="-1"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="mb-0">Notify Parent</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <form
                className="new-form-style"
                onSubmit={handleSubmit(saveData)}
              >
                <div className="row g-3">
                  <div className="col-md-12">
                    <div className="card">
                      <div className="card-body">
                        <div className="row g-3 new-form-style">
                          <div className="col-md-12">
                            <label className="form-label form-label-new">
                              Title<span className="text-danger fs-9">*</span>
                            </label>
                            <input
                              {...register("title", {
                                required: "This field is required.",
                              })}
                              type="text"
                              className="form-control"
                            />
                            {errors.title && (
                              <small className="text-danger">
                                {errors.title.message}
                              </small>
                            )}
                          </div>
                          <div className="col-md-12">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="isImmediate"
                                checked={isImmediate}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setIsImmediate(checked);
                                  if (!checked) {
                                    setSelectedMode("roles");
                                    setValue("roleIds", "");
                                    setValue("userIds", "");
                                    setValue("additionalEmails", "");
                                    setUsersData([]);
                                    setValue("formCategoryId", "");
                                    setValue("FormSubCategoryId", "");
                                    setValue("formId", "");
                                    setValue("notificationFormEventId", "");
                                    setSelectedCategoryId(null);
                                    setSelectedSubCategoryId(null);
                                    setNavFormData([]);
                                    setFormEventsData([]);
                                  }
                                }}
                                disabled={true}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="isImmediate"
                              >
                                Immediate Notification
                              </label>
                            </div>
                          </div>
                          {!isImmediate && (
                            <>
                              <div className="col-md-4">
                                <label className="form-label form-label-new">
                                  Category
                                  <span className="text-danger fs-9">*</span>
                                </label>
                                <Controller
                                  name="formCategoryId"
                                  control={control}
                                  rules={{
                                    required:
                                      !isImmediate && "This field is required.",
                                  }}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      options={categoryData.map((category) => ({
                                        value: category.id,
                                        label: category.name,
                                      }))}
                                      onChange={handleCategoryChange}
                                      className="react-select-style"
                                      classNamePrefix="react-select-style-pref"
                                      isClearable
                                      isDisabled={true}
                                      value={
                                        field.value
                                          ? {
                                              value: field.value,
                                              label:
                                                categoryData.find(
                                                  (c) => c.id === field.value
                                                )?.name || "",
                                            }
                                          : null
                                      }
                                    />
                                  )}
                                />
                                {errors.formCategoryId && (
                                  <small className="text-danger">
                                    {errors.formCategoryId.message}
                                  </small>
                                )}
                              </div>
                              <div className="col-md-4">
                                <label className="form-label form-label-new">
                                  Sub Category
                                  <span className="text-danger fs-9">*</span>
                                </label>
                                <Controller
                                  name="FormSubCategoryId"
                                  control={control}
                                  rules={{
                                    required:
                                      !isImmediate && "This field is required.",
                                  }}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      options={subCategoryData.map(
                                        (subCategory) => ({
                                          value: subCategory.id,
                                          label: subCategory.name,
                                        })
                                      )}
                                      onChange={handleSubCategoryChange}
                                      className="react-select-style"
                                      classNamePrefix="react-select-style-pref"
                                      isClearable
                                      isDisabled={true}
                                      value={
                                        field.value
                                          ? {
                                              value: field.value,
                                              label:
                                                subCategoryData.find(
                                                  (sc) => sc.id === field.value
                                                )?.name || "",
                                            }
                                          : null
                                      }
                                    />
                                  )}
                                />
                                {errors.FormSubCategoryId && (
                                  <small className="text-danger">
                                    {errors.FormSubCategoryId.message}
                                  </small>
                                )}
                              </div>
                              <div className="col-md-4">
                                <label className="form-label form-label-new">
                                  Form
                                  <span className="text-danger fs-9">*</span>
                                </label>
                                <Controller
                                  name="formId"
                                  control={control}
                                  rules={{
                                    required:
                                      !isImmediate && "This field is required.",
                                  }}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      options={navFormData.map((form) => ({
                                        value: form.id,
                                        label: form.name,
                                      }))}
                                      onChange={handleFormChange}
                                      className="react-select-style"
                                      classNamePrefix="react-select-style-pref"
                                      isClearable
                                      isDisabled={true}
                                      value={
                                        field.value
                                          ? {
                                              value: field.value,
                                              label:
                                                navFormData.find(
                                                  (f) => f.id === field.value
                                                )?.name || "",
                                            }
                                          : null
                                      }
                                    />
                                  )}
                                />
                                {errors.formId && (
                                  <small className="text-danger">
                                    {errors.formId.message}
                                  </small>
                                )}
                              </div>
                              <div className="col-md-12">
                                <label className="form-label form-label-new">
                                  Form Event
                                  <span className="text-danger fs-9">*</span>
                                </label>
                                <Controller
                                  name="notificationFormEventId"
                                  control={control}
                                  rules={{
                                    required:
                                      !isImmediate && "This field is required.",
                                  }}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      options={formEventsData.map((item) => ({
                                        value: item.id,
                                        label: item.title,
                                      }))}
                                      onChange={handleFormEventChange}
                                      className="react-select-style"
                                      classNamePrefix="react-select-style-pref"
                                      isClearable
                                      isDisabled={true}
                                      value={
                                        field.value
                                          ? {
                                              value: field.value,
                                              label:
                                                formEventsData.find(
                                                  (fe) => fe.id === field.value
                                                )?.title || "",
                                            }
                                          : null
                                      }
                                    />
                                  )}
                                />
                                {errors.notificationFormEventId && (
                                  <small className="text-danger">
                                    {errors.notificationFormEventId.message}
                                  </small>
                                )}
                              </div>
                            </>
                          )}
                          <div className="col-md-12">
                            <label className="form-label">Select Mode</label>
                            <div className="form-check form-check-inline">
                              <input
                                type="radio"
                                className="form-check-input"
                                id="modeRoles"
                                name="mode"
                                value="roles"
                                checked={selectedMode === "roles"}
                                onChange={() => {
                                  setSelectedMode("roles");
                                  setValue("roleIds", "");
                                  setValue("userIds", "");
                                  setUsersData([]);
                                }}
                                disabled={true}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="modeRoles"
                              >
                                Roles
                              </label>
                            </div>
                            <div className="form-check form-check-inline">
                              <input
                                type="radio"
                                className="form-check-input"
                                id="modeUsers"
                                name="mode"
                                value="users"
                                checked={selectedMode === "users"}
                                disabled={true}
                                onChange={() => {
                                  setSelectedMode("users");
                                  setValue("roleIds", "");
                                  setValue("userIds", "");
                                  setUsersData([]);
                                }}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="modeUsers"
                              >
                                Users
                              </label>
                            </div>
                          </div>
                          {selectedMode === "roles" && (
                            <div className="col-md-12">
                              <label className="form-label form-label-new">
                                Roles
                              </label>
                              <Controller
                                name="roleIds"
                                control={control}
                                render={({ field }) => {
                                  const selectedIds =
                                    field.value &&
                                    typeof field.value === "string"
                                      ? field.value
                                          .split(",")
                                          .filter(Boolean)
                                          .map((id) => parseInt(id))
                                      : [];
                                  return (
                                    <Select
                                      {...field}
                                      options={rolesData
                                        .filter(
                                          (item) =>
                                            !selectedIds.includes(item.id)
                                        )
                                        .map((item) => ({
                                          value: item.id,
                                          label: item.name,
                                        }))}
                                      onChange={handleRoleChange}
                                      className="react-select-style"
                                      classNamePrefix="react-select-style-pref"
                                      isMulti
                                      isDisabled={true}
                                      value={
                                        field.value &&
                                        typeof field.value === "string"
                                          ? field.value
                                              .split(",")
                                              .filter(Boolean)
                                              .map((id) => ({
                                                value: id,
                                                label:
                                                  rolesData.find(
                                                    (nt) =>
                                                      nt.id === parseInt(id)
                                                  )?.name || "",
                                              }))
                                          : []
                                      }
                                    />
                                  );
                                }}
                              />
                              {errors.roleIds && (
                                <small className="text-danger">
                                  {errors.roleIds.message}
                                </small>
                              )}
                            </div>
                          )}
                          {selectedMode === "users" && (
                            <>
                              <div className="col-md-12">
                                <label className="form-label form-label-new">
                                  Role
                                </label>
                                <Controller
                                  name="roleIds"
                                  control={control}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      options={rolesData.map((item) => ({
                                        value: item.id,
                                        label: item.name,
                                      }))}
                                      onChange={handleRoleChange}
                                      className="react-select-style"
                                      classNamePrefix="react-select-style-pref"
                                      isDisabled={true}
                                      value={
                                        field.value &&
                                        typeof field.value === "string"
                                          ? {
                                              value: field.value,
                                              label:
                                                rolesData.find(
                                                  (nt) =>
                                                    nt.id ===
                                                    parseInt(field.value)
                                                )?.name || "",
                                            }
                                          : null
                                      }
                                    />
                                  )}
                                />
                                {errors.roleIds && (
                                  <small className="text-danger">
                                    {errors.roleIds.message}
                                  </small>
                                )}
                              </div>
                            </>
                          )}
                          <div className="col-md-12">
                            <label className="form-label form-label-new">
                              Source<span className="text-danger fs-9">*</span>
                            </label>
                            <Controller
                              name="notificationTemplates"
                              control={control}
                              rules={{
                                validate: (value) =>
                                  value.length > 0 ||
                                  "At least one source is required.",
                              }}
                              render={({ field }) => (
                                <Select
                                  options={notifTypesData.map((item) => ({
                                    value: item.id,
                                    label: item.title,
                                  }))}
                                  onChange={(selectedOptions) =>
                                    handleNotificationTypeChange(
                                      selectedOptions
                                    )
                                  }
                                  className="react-select-style"
                                  classNamePrefix="react-select-style-pref"
                                  isMulti
                                  isDisabled={isEditing} // Keep disabled when editing
                                  value={notifTypesData
                                    .filter((nt) =>
                                      field.value.some(
                                        (t) => t.notificationTypeId === nt.id
                                      )
                                    )
                                    .map((nt) => ({
                                      value: nt.id,
                                      label: nt.title,
                                    }))}
                                />
                              )}
                            />
                            {errors.notificationTemplates && (
                              <small className="text-danger">
                                {errors.notificationTemplates.message}
                              </small>
                            )}
                          </div>
                          {notificationTypeIds.map((typeId, index) => {
                            const isEmail =
                              notifTypesData
                                .find((type) => type.id === typeId)
                                ?.title.toLowerCase() === "email";
                            return (
                              <div className="col-md-12" key={typeId}>
                                <fieldset className="border p-3 mb-0 rounded">
                                  <legend className="w-auto fs-8 fw-bold mb-0">
                                    {
                                      notifTypesData.find(
                                        (type) => type.id === typeId
                                      )?.title
                                    }{" "}
                                    Template
                                  </legend>
                                  <div className="row g-3">
                                    {isEmail && (
                                      <>
                                        <div className="col-md-12">
                                          <label className="form-label form-label-new">
                                            User Emails
                                          </label>
                                          <Controller
                                            name="userIds"
                                            control={control}
                                            rules={{
                                              validate: (value) => {
                                                if (!value) return true;
                                                const emails = value
                                                  .split(",")
                                                  .map((email) => email.trim());
                                                const emailPattern =
                                                  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                                                const allValid = emails.every(
                                                  (email) =>
                                                    emailPattern.test(email)
                                                );
                                                return (
                                                  allValid ||
                                                  "Invalid email format"
                                                );
                                              },
                                            }}
                                            render={({ field }) => (
                                              <input
                                                {...field}
                                                type="text"
                                                placeholder="Enter user emails separated by commas"
                                                className="form-control"
                                                value={field.value || ""}
                                                onChange={(e) =>
                                                  field.onChange(e.target.value)
                                                }
                                              />
                                            )}
                                          />
                                          <div className="fs-10 text-black-50">
                                            (comma separated)
                                          </div>
                                          {errors.userIds && (
                                            <small className="text-danger">
                                              {errors.userIds.message}
                                            </small>
                                          )}
                                        </div>

                                        <div className="col-md-12">
                                          <label className="form-label form-label-new">
                                            Additional Email(s)
                                          </label>
                                          <input
                                            type="text"
                                            {...register("additionalEmails", {
                                              validate: (value) => {
                                                if (!value) return true;
                                                const emails = value
                                                  .split(",")
                                                  .map((email) => email.trim());
                                                const emailPattern =
                                                  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                                                const allValid = emails.every(
                                                  (email) =>
                                                    emailPattern.test(email)
                                                );
                                                return (
                                                  allValid ||
                                                  "Invalid email format"
                                                );
                                              },
                                            })}
                                            className="form-control"
                                          />
                                          <div className="fs-10 text-black-50">
                                            (comma separated)
                                          </div>
                                          {errors.additionalEmails && (
                                            <small className="text-danger">
                                              {errors.additionalEmails.message}
                                            </small>
                                          )}
                                        </div>

                                        <div className="col-md-12">
                                          <label className="form-label form-label-new">
                                            Subject
                                            <span className="text-danger fs-9">
                                              *
                                            </span>
                                          </label>
                                          <input
                                            {...register(
                                              `notificationTemplates.${index}.subject`,
                                              {
                                                required:
                                                  "This field is required.",
                                              }
                                            )}
                                            type="text"
                                            className="form-control"
                                          />
                                          {errors.notificationTemplates?.[index]
                                            ?.subject && (
                                            <small className="text-danger">
                                              {
                                                errors.notificationTemplates[
                                                  index
                                                ].subject.message
                                              }
                                            </small>
                                          )}
                                        </div>
                                      </>
                                    )}

                                    <div className="col-md-12">
                                      <label className="form-label form-label-new">
                                        Placeholder
                                      </label>
                                      <Controller
                                        name="placeholderId"
                                        control={control}
                                        render={({ field }) => (
                                          <Select
                                            {...field}
                                            options={placeholdersData.map(
                                              (item) => ({
                                                value: item.id,
                                                label: item.placeholderTitle,
                                              })
                                            )}
                                            onChange={handlePlaceholderChange}
                                            className="react-select-style"
                                            classNamePrefix="react-select-style-pref"
                                            isClearable
                                            value={
                                              field.value
                                                ? {
                                                    value: field.value,
                                                    label:
                                                      placeholdersData.find(
                                                        (nt) =>
                                                          nt.id === field.value
                                                      )?.placeholderTitle || "",
                                                  }
                                                : null
                                            }
                                          />
                                        )}
                                      />
                                    </div>

                                    <div className="col-md-12">
                                      <label className="form-label form-label-new">
                                        Template
                                        <span className="text-danger fs-9">
                                          *
                                        </span>
                                      </label>
                                      <Controller
                                        name={`notificationTemplates.${index}.body`}
                                        control={control}
                                        rules={{
                                          required: "This field is required.",
                                        }}
                                        render={({ field }) => (
                                          <div>
                                            <ReactQuill
                                              ref={(el) =>
                                                (quillRefs.current[index] = el)
                                              }
                                              theme="snow"
                                              modules={modules}
                                              value={field.value || ""}
                                              onChange={(value) =>
                                                field.onChange(value)
                                              }
                                              className="bg-white"
                                              onFocus={() =>
                                                handleEditorFocus(index)
                                              }
                                            />
                                          </div>
                                        )}
                                      />
                                      {errors.notificationTemplates?.[index]
                                        ?.body && (
                                        <small className="text-danger">
                                          {
                                            errors.notificationTemplates[index]
                                              .body.message
                                          }
                                        </small>
                                      )}
                                    </div>

                                    <input
                                      type="hidden"
                                      {...register(
                                        `notificationTemplates.${index}.notificationTypeId`
                                      )}
                                      value={typeId}
                                    />
                                  </div>
                                </fieldset>
                              </div>
                            );
                          })}
                          <div className="col-md-12">
                            <div className="form-check">
                              <input
                                {...register("isActive")}
                                type="checkbox"
                                className="form-check-input"
                                id="isActive"
                                disabled={true}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="isActive"
                              >
                                Active
                              </label>
                            </div>
                          </div>
                          <div className="col-12 mt-3">
                            <button
                              className="btn btn-primary btn-sm"
                              type="submit"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <span
                                  className="spinner-border spinner-custom-size me-1"
                                  aria-hidden="true"
                                ></span>
                              ) : null}
                              <span role="status">
                                {isImmediate ? "Send Now!" : "Save"}
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}
export default AttendanceNotificationModal;
